import React, { useState } from 'react';
import {
  Box,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { useLocation, useNavigate } from 'react-router-dom';
import sidebarConfig from 'src/layouts/dashboard/SidebarConfig';
import useAuth from 'src/hooks/useAuth';

export default function MobileBottomNavBar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentChildren, setCurrentChildren] = useState([]);

  const handleOpenPopover = (event, children) => {
    setAnchorEl(event.currentTarget);
    setCurrentChildren(children);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setCurrentChildren([]);
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleClosePopover();
  };

  if (!isMobile) return null;

  const navItems = sidebarConfig.flatMap((group) =>
    group.items.filter((item) => !item.hidden || !item.hidden(user))
  );

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-around',
          zIndex: 1300,
          pt: 1.2,
          pb: 'calc(9.6px + env(safe-area-inset-bottom, 0px))',
          px: 1,
          overflowX: 'auto',
          overflowY: 'none',
        }}
      >
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const hasChildren = item.children && item.children.length > 0;
          const filteredChildren = item.children?.filter((c) => !c.hidden || !c.hidden(user)) || [];

          return (
            <Box
              key={item.title}
              onClick={(e) =>
                hasChildren ? handleOpenPopover(e, filteredChildren) : handleNavigate(item.path)
              }
              sx={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                mt: -0.5,
                WebkitTapHighlightColor: 'transparent',
                userSelect: 'none',
                outline: 'none',
              }}
            >

              <Box
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: isActive ? 'primary.main' : 'action.selected',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '30%',
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    color: isActive ? 'common.white' : 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </Box>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'primary.main' : 'text.secondary',
                  textTransform: 'capitalize',
                }}
              >
                {item.title === 'production control board' ? 'PCB' : item.title}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        PaperProps={{
          sx: {
            mt: -1,
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <List dense sx={{ minWidth: 180, pl: 1, pr: 1 }}>
          {currentChildren.map((child) => {
            const isActiveChild = location.pathname.startsWith(child.path);

            return (
              <ListItem
                button
                key={child.title}
                onClick={() => handleNavigate(child.path)}
                selected={isActiveChild}
                sx={{
                  px: 1.5,
                  py: 1,
                  mb: 0.5,
                  borderRadius: 0.8,
                  bgcolor: isActiveChild ? 'primary.main' : 'action.selected',
                  color: isActiveChild ? 'common.white' : 'text.secondary',
                  '&:last-of-type': {
                    mb: 0
                  },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'common.white'
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1
                  }}
                >
                  {child.icon}
                </Box>
                <ListItemText
                  primary={
                    child.title.charAt(0).toUpperCase() + child.title.slice(1)
                  }
                  primaryTypographyProps={{ fontSize: 13 }}
                />
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
}
